import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Building, BarChart3, Newspaper } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TargetAudienceProps } from '@/types';

/**
 * Target Audience Section Component
 * Displays who should apply for early access
 */
export function TargetAudience({ data, className }: TargetAudienceProps) {
  const getIcon = (index: number) => {
    const icons = [Users, Building, BarChart3, Newspaper];
    const IconComponent = icons[index];
    return <IconComponent className="w-8 h-8 text-primary" />;
  };

  return (
    <section id="target-audience" className={cn("py-20 bg-gradient-subtle", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gradient">
            {data.title}
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join our exclusive early access program and be among the first to experience the future of AI-powered infrastructure management.
          </p>
        </div>

        {/* Target Categories Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
          {data.categories.map((category, index) => (
            <Card key={index} className="gradient-card border-border/10 shadow-card hover:shadow-glow transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                      {getIcon(index)}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-foreground">
                      {category.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {category.description}
                    </p>
                    <Button 
                      variant="outline" 
                      className="border-primary/20 hover:bg-primary/5 hover:border-primary/40"
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="gradient-card border-primary/20 shadow-glow max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-muted-foreground mb-6">
                Apply now for early access and join the future of AI-powered infrastructure management.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 shadow-glow"
                >
                  Apply for Early Access
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-primary/20 hover:bg-primary/5"
                >
                  Schedule a Demo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
